import type { Fen, GameResult, Move, PieceChar, PositionSnapshot, San, Turn } from '@strategy4kids/protocol';
import { PROMOTION_RANK } from './promotion.js';
import type { AttackTable } from './attack-table.js';
import { IllegalMoveError } from './chess.js';
import { type Placement, colorOf, indexToSquare, parsePlacement, squareToIndex } from './coords.js';
import type { GameAdapter, MoveTarget } from './game-adapter.js';
import type { AttackGen } from './shields.js';

export const CHECKERS_START_FEN =
  '1m1m1m1m/m1m1m1m1/1m1m1m1m/8/8/M1M1M1M1/1M1M1M1M/M1M1M1M1 w - - 0 1';

export interface CheckersMove {
  /** Starting square. */
  readonly from: string;
  /** Final landing square (after the full multi-jump chain). */
  readonly to: string;
  /** Every square visited, from → … → to. */
  readonly path: string[];
  /** Squares of captured pieces, in capture order. */
  readonly captured: string[];
  /** True when a man is promoted to king by this move. */
  readonly promotion: boolean;
}

const MAN_DIRS: Record<Turn, ReadonlyArray<readonly [number, number]>> = {
  w: [
    [-1, -1],
    [-1, 1],
  ],
  b: [
    [1, -1],
    [1, 1],
  ],
};

const KING_DIRS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [-1, 1],
  [1, -1],
  [1, 1],
];

function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < 8 && c >= 0 && c < 8;
}

function isKing(char: string): boolean {
  return char.toLowerCase() === 'x';
}

function other(turn: Turn): Turn {
  return turn === 'w' ? 'b' : 'w';
}

/**
 * A man is promoted when its path touches its far rank, as a **row index**
 * (row 0 is rank 8, per FEN ordering).
 *
 * The rank itself lives in `PROMOTION_RANK` and is the single source of truth
 * for the whole federation — the power-map overlay reads it to mark the goal
 * row, which scores zero on that map and would otherwise tell a child the
 * promotion row is worthless. This helper exists only to convert it into the
 * index the loops below already use.
 */
function promotionRankIndex(color: Turn): number {
  return 8 - PROMOTION_RANK[color];
}

/** All complete capture chains starting at the piece on index `i`. */
function captureChains(placement: Placement, i: number, color: Turn): CheckersMove[] {
  const enemy = other(color);
  const out: CheckersMove[] = [];
  const visited = new Set<number>([i]);
  const path: number[] = [i];
  const captured: number[] = [];
  const startKing = isKing(placement[i] ?? '');

  const dfs = (cur: number, kingNow: boolean): void => {
    // American rule: a man that reaches the last rank mid-chain becomes a king.
    const effectiveKing = kingNow || Math.floor(cur / 8) === promotionRankIndex(color);
    let extended = false;
    const curR = Math.floor(cur / 8);
    const curC = cur % 8;
    for (const [dr, dc] of effectiveKing ? KING_DIRS : MAN_DIRS[color]) {
      const er = curR + dr;
      const ec = curC + dc;
      const lr = er + dr;
      const lc = ec + dc;
      if (!inBounds(lr, lc)) continue;
      const enemyIdx = er * 8 + ec;
      const landIdx = lr * 8 + lc;
      const enemyChar = placement[enemyIdx];
      if (enemyChar == null || colorOf(enemyChar) !== enemy) continue;
      if (placement[landIdx] != null) continue;
      if (visited.has(landIdx)) continue;
      visited.add(landIdx);
      path.push(landIdx);
      captured.push(enemyIdx);
      dfs(landIdx, effectiveKing);
      captured.pop();
      path.pop();
      visited.delete(landIdx);
      extended = true;
    }
    if (!extended && captured.length > 0) {
      const end = path[path.length - 1] as number;
      out.push({
        from: indexToSquare(i),
        to: indexToSquare(end),
        path: path.map((x) => indexToSquare(x)),
        captured: captured.map((x) => indexToSquare(x)),
        promotion: !startKing && path.some((x) => Math.floor(x / 8) === promotionRankIndex(color)),
      });
    }
  };

  dfs(i, startKing);
  return out;
}

/** Single-step non-capture moves for the piece on index `i`. */
function simpleMoves(placement: Placement, i: number, color: Turn): CheckersMove[] {
  const king = isKing(placement[i] ?? '');
  const out: CheckersMove[] = [];
  const r = Math.floor(i / 8);
  const c = i % 8;
  for (const [dr, dc] of king ? KING_DIRS : MAN_DIRS[color]) {
    const rr = r + dr;
    const cc = c + dc;
    if (!inBounds(rr, cc)) continue;
    if (placement[rr * 8 + cc] != null) continue;
    const to = indexToSquare(rr * 8 + cc);
    out.push({
      from: indexToSquare(i),
      to,
      path: [indexToSquare(i), to],
      captured: [],
      promotion: !king && rr === promotionRankIndex(color),
    });
  }
  return out;
}

/**
 * All legal moves for `turn`. Captures are forced: if any capture exists, only
 * captures are legal. Results are deduped by (from, to) so `makeMove` can resolve
 * a from→to pair to exactly one path.
 */
export function legalMovesOf(placement: Placement, turn: Turn): CheckersMove[] {
  const captures: CheckersMove[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null || colorOf(char) !== turn) continue;
    for (const m of captureChains(placement, i, turn)) {
      const key = `${m.from}${m.to}`;
      if (seen.has(key)) continue;
      seen.add(key);
      captures.push(m);
    }
  }
  if (captures.length > 0) return captures;
  const simples: CheckersMove[] = [];
  const seenSimple = new Set<string>();
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null || colorOf(char) !== turn) continue;
    for (const m of simpleMoves(placement, i, turn)) {
      const key = `${m.from}${m.to}`;
      if (seenSimple.has(key)) continue;
      seenSimple.add(key);
      simples.push(m);
    }
  }
  return simples;
}

/** Apply a (possibly multi-jump) checkers move to a placement; returns a new placement. */
export function applyCheckersMove(placement: Placement, move: CheckersMove): Placement {
  const next = [...placement] as Placement;
  const startSq = move.path[0];
  const endSq = move.path[move.path.length - 1];
  if (startSq == null || endSq == null) return placement;
  const start = squareToIndex(startSq);
  const end = squareToIndex(endSq);
  const moving = next[start];
  if (moving == null) return placement;
  const wasKing = moving.toLowerCase() === 'x';
  let char = moving;
  if (move.promotion && !wasKing) char = moving === moving.toUpperCase() ? 'X' : 'x';
  next[start] = null;
  for (const sq of move.captured) next[squareToIndex(sq)] = null;
  next[end] = char;
  return next;
}

/** Rebuild a FEN placement string + turn from a placement (halfmove/fullmove fixed). */
export function placementToFen(placement: Placement, turn: Turn): string {
  const ranks: string[] = [];
  for (let r = 0; r < 8; r++) {
    let rank = '';
    let empty = 0;
    for (let c = 0; c < 8; c++) {
      const ch = placement[r * 8 + c];
      if (ch == null) {
        empty += 1;
      } else {
        if (empty > 0) {
          rank += String(empty);
          empty = 0;
        }
        rank += ch;
      }
    }
    if (empty > 0) rank += String(empty);
    ranks.push(rank);
  }
  return `${ranks.join('/')} ${turn} - - 0 1`;
}

function countPieces(placement: Placement): { w: number; b: number } {
  let w = 0;
  let b = 0;
  for (let i = 0; i < 64; i++) {
    const ch = placement[i];
    if (ch == null) continue;
    if (colorOf(ch) === 'w') w += 1;
    else b += 1;
  }
  return { w, b };
}

/**
 * Reject clearly-malformed checkers FENs: the placement must be 8 ranks × 8 files
 * built from digits, `/`, and the piece chars `mMxX`. (Full FEN grammar — turn token,
 * counters — is not validated; this only rejects structurally broken input.)
 */
function isValidFen(fen: Fen): boolean {
  const ranks = fen.split(' ')[0]?.split('/') ?? [];
  if (ranks.length !== 8) return false;
  for (const rank of ranks) {
    if (rank === '') return false;
    let files = 0;
    for (const ch of rank) {
      if (ch >= '1' && ch <= '8') files += Number(ch);
      else if (ch === 'm' || ch === 'M' || ch === 'x' || ch === 'X') files += 1;
      else return false;
    }
    if (files !== 8) return false;
  }
  return true;
}

/** Checkers game result: elimination (empty side) or immobility (side to move has no moves). */
export function resultOf(placement: Placement, turn: Turn): GameResult | null {
  const { w, b } = countPieces(placement);
  if (w === 0) return { kind: 'elimination', winner: 'b' };
  if (b === 0) return { kind: 'elimination', winner: 'w' };
  if (legalMovesOf(placement, turn).length === 0) {
    return { kind: 'immobility', winner: other(turn) };
  }
  return null;
}

/** Material score; positive = white better. Man = 100, king = 300. */
export function evaluatePlacement(placement: Placement): number {
  let score = 0;
  for (let i = 0; i < 64; i++) {
    const ch = placement[i];
    if (ch == null) continue;
    const v = ch.toLowerCase() === 'x' ? 300 : 100;
    score += colorOf(ch) === 'w' ? v : -v;
  }
  return score;
}

/** Squares a checkers piece on index `i` controls (move destinations), as a bitmask over a fresh AttackTable. */
export function checkersAttackTable(placement: Placement): AttackTable {
  const table: AttackTable = { white: new Uint8Array(64), black: new Uint8Array(64) };
  for (let i = 0; i < 64; i++) {
    const ch = placement[i];
    if (ch == null) continue;
    const color = colorOf(ch);
    const king = ch.toLowerCase() === 'x';
    const r = Math.floor(i / 8);
    const c = i % 8;
    for (const [dr, dc] of king ? KING_DIRS : MAN_DIRS[color]) {
      const rr = r + dr;
      const cc = c + dc;
      if (!inBounds(rr, cc)) continue;
      const t = rr * 8 + cc;
      if (color === 'w') table.white[t] = 1;
      else table.black[t] = 1;
    }
  }
  return table;
}

/**
 * Squares a checkers piece on index `i` attacks, as board indices. Backs the
 * `AttackGen` seam in shields.ts, so the shield indicator can tell which piece
 * is defending which.
 *
 * Deliberately NOT the same as `checkersAttackTable` above, even though the
 * piece-direction constants are shared. That table answers "where can this
 * piece legally LAND next", and a man and a king are both one step for that
 * purpose. This answers "which squares does this piece guard", and a king in
 * American checkers flies — it genuinely defends a friendly piece four squares
 * away down a diagonal. Modelling that as a single step would under-report
 * exactly the long-range defences a kid most needs to see.
 *
 * Man: the two forward diagonals, one step. King: all four diagonals, ray-walked,
 * stopping after the first occupied square (that square is defended, the ones
 * behind it are not).
 *
 * Do not use `squaresAttacked` from attack-table.ts here. It has no 'm'/'x'
 * branch and falls through to flying-queen movement.
 */
export function checkersSquaresAttacked(placement: Placement, i: number): number[] {
  const char = placement[i];
  if (char == null) return [];
  const color = colorOf(char);
  const king = isKing(char);
  const r = Math.floor(i / 8);
  const c = i % 8;
  const out: number[] = [];
  for (const [dr, dc] of king ? KING_DIRS : MAN_DIRS[color]) {
    let rr = r + dr;
    let cc = c + dc;
    while (inBounds(rr, cc)) {
      out.push(rr * 8 + cc);
      if (!king) break;
      if (placement[rr * 8 + cc] != null) break;
      rr += dr;
      cc += dc;
    }
  }
  return out;
}

/** Piece-weighted control score at a square; positive = white. */
export function checkersControlScore(placement: Placement, target: number): number {
  let score = 0;
  for (let i = 0; i < 64; i++) {
    const ch = placement[i];
    if (ch == null) continue;
    const color = colorOf(ch);
    const king = ch.toLowerCase() === 'x';
    const r = Math.floor(i / 8);
    const c = i % 8;
    let controls = false;
    for (const [dr, dc] of king ? KING_DIRS : MAN_DIRS[color]) {
      const rr = r + dr;
      const cc = c + dc;
      if (inBounds(rr, cc) && rr * 8 + cc === target) {
        controls = true;
        break;
      }
    }
    if (!controls) continue;
    score += color === 'w' ? (king ? 300 : 100) : -(king ? 300 : 100);
  }
  return score;
}

function moveSan(move: CheckersMove): San {
  return move.path.join('-');
}

export class CheckersAdapter implements GameAdapter {
  readonly gameId = 'checkers';

  /** Full move list keyed by resulting FEN (a FEN-built snapshot has no history). */
  private readonly historyByFen = new Map<string, Move[]>();

  newGame(): PositionSnapshot {
    return this.fromFen(CHECKERS_START_FEN);
  }

  fromFen(fen: Fen): PositionSnapshot {
    this.historyByFen.clear();
    const placement = parsePlacement(fen);
    if (!isValidFen(fen)) throw new IllegalMoveError('invalid checkers FEN');
    const turn = (fen.split(' ')[1] ?? 'w') as Turn;
    const moves = legalMovesOf(placement, turn);
    return {
      fen,
      turn,
      legalMoves: moves.map(moveSan),
      result: resultOf(placement, turn),
      captured: { w: [], b: [] },
    };
  }

  legalMoves(pos: PositionSnapshot): San[] {
    return pos.legalMoves;
  }

  legalMovesForSquare(pos: PositionSnapshot, from: string): MoveTarget[] {
    const placement = parsePlacement(pos.fen);
    return legalMovesOf(placement, pos.turn)
      .filter((m) => m.from === from)
      .map((m) => ({ to: m.to, capture: m.captured.length > 0 }));
  }

  makeMove(pos: PositionSnapshot, from: string, to: string): PositionSnapshot {
    const placement = parsePlacement(pos.fen);
    const move = legalMovesOf(placement, pos.turn).find((m) => m.from === from && m.to === to);
    if (!move) throw new IllegalMoveError(`${from}-${to}`);
    const moving = placement[squareToIndex(from)];
    if (moving == null) throw new IllegalMoveError(`${from}-${to}`);
    const firstCaptured = move.captured[0];
    const nextPlacement = applyCheckersMove(placement, move);
    const nextTurn = other(pos.turn);
    const fen = placementToFen(nextPlacement, nextTurn);
    const mv: Move = {
      from,
      to,
      san: moveSan(move),
      color: pos.turn,
      piece: moving.toLowerCase() as Lowercase<PieceChar>,
      captured:
        firstCaptured != null
          ? ((placement[squareToIndex(firstCaptured)] as PieceChar).toLowerCase() as PieceChar)
          : undefined,
      promotion: move.promotion ? 'x' : undefined,
      flags: move.captured.length > 0 ? 'c' : move.promotion ? 'p' : 'n',
    };
    const prior = this.historyByFen.get(pos.fen) ?? [];
    this.historyByFen.set(fen, [...prior, mv]);
    const captured: { w: PieceChar[]; b: PieceChar[] } = {
      w: [...pos.captured.w],
      b: [...pos.captured.b],
    };
    for (const sq of move.captured) {
      const ch = placement[squareToIndex(sq)];
      if (ch == null) continue;
      // ChessAdapter convention: route captures by the mover's color — captured.w is
      // what white has taken. Chars are lowercased to type-only (matches chess).
      const list = pos.turn === 'w' ? captured.w : captured.b;
      list.push(ch.toLowerCase() as PieceChar);
    }
    const result = resultOf(nextPlacement, nextTurn);
    return {
      fen,
      turn: nextTurn,
      legalMoves: legalMovesOf(nextPlacement, nextTurn).map(moveSan),
      result,
      captured,
    };
  }

  result(pos: PositionSnapshot): GameResult | null {
    return pos.result;
  }

  evaluate(pos: PositionSnapshot): number {
    return evaluatePlacement(parsePlacement(pos.fen));
  }

  attackTable(pos: PositionSnapshot): AttackTable {
    return checkersAttackTable(parsePlacement(pos.fen));
  }

  controlScore(pos: PositionSnapshot, sq: string): number {
    return checkersControlScore(parsePlacement(pos.fen), squareToIndex(sq));
  }

  attackGen(): AttackGen {
    return checkersSquaresAttacked;
  }

  history(pos: PositionSnapshot): Move[] {
    return this.historyByFen.get(pos.fen) ?? [];
  }

  pieceAt(pos: PositionSnapshot, sq: string): { char: PieceChar; color: Turn } | null {
    const char = parsePlacement(pos.fen)[squareToIndex(sq)];
    if (char == null) return null;
    return { char: char.toLowerCase() as 'm' | 'x', color: colorOf(char) };
  }

  isCheck(_pos: PositionSnapshot): boolean {
    return false;
  }

  kingSquare(_pos: PositionSnapshot, _color: Turn): string | null {
    return null;
  }
}

export { parsePlacement };
